<script setup lang="ts">
import { ref } from 'vue'
import { apiErrorMessage } from '@/api'
import { onClickOutside } from '@vueuse/core'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()

const open = ref(false)
const menuRef = ref<HTMLElement | null>(null)
onClickOutside(menuRef, () => {
  open.value = false
})

const creating = ref(false)
const newWorkspaceName = ref('')
const createError = ref<string | null>(null)
const submitting = ref(false)

function selectWorkspace(workspaceId: string) {
  auth.setActiveWorkspace(workspaceId)
  open.value = false
}

function startCreating() {
  creating.value = true
  createError.value = null
  newWorkspaceName.value = ''
}

async function submitCreate() {
  createError.value = null
  submitting.value = true
  try {
    await auth.createWorkspace(newWorkspaceName.value)
    creating.value = false
    open.value = false
  } catch (e: unknown) {
    createError.value = apiErrorMessage(e, 'Unable to create workspace.')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div
    ref="menuRef"
    class="relative"
  >
    <button
      type="button"
      class="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border-hairline bg-surface-raised px-3 text-sm text-on-surface hover:border-accent-electric"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="open = !open"
    >
      <span class="truncate">{{ auth.activeWorkspace?.name ?? 'Select workspace' }}</span>
      <span
        aria-hidden="true"
        class="text-text-secondary"
      >▾</span>
    </button>

    <div
      v-if="open"
      role="listbox"
      class="absolute left-0 top-12 z-10 w-64 rounded-md border border-border-hairline bg-surface-raised p-1 shadow-lg"
    >
      <button
        v-for="workspace in auth.workspaces"
        :key="workspace.id"
        type="button"
        role="option"
        :aria-selected="workspace.id === auth.activeWorkspaceId"
        class="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm text-on-surface hover:bg-surface-container-high"
        @click="selectWorkspace(workspace.id)"
      >
        <span class="truncate">{{ workspace.name }}</span>
        <span
          v-if="workspace.id === auth.activeWorkspaceId"
          class="text-accent-electric"
        >✓</span>
      </button>

      <div class="my-1 border-t border-border-hairline" />

      <template v-if="!creating">
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-accent-electric hover:bg-surface-container-high"
          @click="startCreating"
        >
          + New workspace
        </button>
      </template>
      <form
        v-else
        class="space-y-2 p-2"
        @submit.prevent="submitCreate"
      >
        <label for="new-workspace-name">
          <span class="sr-only">Workspace name</span>
          <input
            id="new-workspace-name"
            v-model="newWorkspaceName"
            type="text"
            required
            placeholder="Workspace name"
            class="h-9 w-full rounded border border-border-hairline bg-transparent px-3 text-sm text-on-surface placeholder:text-outline-variant focus:border-accent-electric focus:outline-none"
          >
        </label>
        <div class="flex gap-2">
          <button
            type="submit"
            :disabled="submitting"
            class="h-8 flex-1 rounded bg-accent-electric text-xs font-bold text-on-primary disabled:opacity-50"
          >
            {{ submitting ? 'Creating…' : 'Create' }}
          </button>
          <button
            type="button"
            class="h-8 flex-1 rounded border border-border-hairline text-xs text-on-surface"
            @click="creating = false"
          >
            Cancel
          </button>
        </div>
        <p
          v-if="createError"
          class="text-xs text-status-error"
        >
          {{ createError }}
        </p>
      </form>
    </div>
  </div>
</template>